import { Alert, Card, Col, Progress, Row, Skeleton, Statistic, Table, Tag, Typography } from 'antd';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { errorMessage } from '../../api/client';
import { useInvestorDashboard } from '../../api/hooks';
import type { RecentTransaction } from '../../types';
import { date, money, percent } from '../../utils/format';

const TYPE_COLOR: Record<string, string> = {
  Investment: 'blue',
  Withdrawal: 'orange',
  'Profit Payment': 'green',
};

/**
 * The investor's own summary. Every value comes from /investors/{id}/dashboard, which is filtered
 * by investor id server-side — no company-wide totals are exposed here.
 */
export default function MyDashboard({ investorId }: { investorId: number }) {
  const { data, isLoading, error } = useInvestorDashboard(investorId);

  if (error) return <Alert type="error" showIcon message={errorMessage(error)} />;
  if (isLoading || !data) return <Skeleton active paragraph={{ rows: 6 }} />;

  const paidShare =
    data.totalAccruedProfit > 0
      ? Math.min(100, Math.round((data.totalPaidProfit / data.totalAccruedProfit) * 100))
      : 0;

  return (
    <>
      {/* headline: what my money is doing */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="My current capital"
              value={money(data.currentPrincipal)}
              prefix="PKR"
              valueStyle={{ fontSize: 20, color: '#1677ff' }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {data.activeTranches} active investment{data.activeTranches === 1 ? '' : 's'}
              {data.averageProfitRate > 0 && ` · avg ${percent(data.averageProfitRate)}`}
            </Typography.Text>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Profit outstanding"
              value={money(data.outstandingProfit)}
              prefix="PKR"
              valueStyle={{ fontSize: 20, color: data.outstandingProfit > 0 ? '#cf1322' : '#3f8600' }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Earned but not yet paid to me
            </Typography.Text>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Earning today"
              value={money(data.todayProfit)}
              prefix="PKR"
              valueStyle={{ fontSize: 20, color: '#3f8600' }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              This month so far: PKR {money(data.thisMonthAccruedProfit)}
            </Typography.Text>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Profit received"
              value={money(data.totalPaidProfit)}
              prefix="PKR"
              valueStyle={{ fontSize: 20 }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              This month: PKR {money(data.thisMonthPaidProfit)}
            </Typography.Text>
          </Card>
        </Col>
      </Row>

      {/* the story of the account */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={10}>
          <Card title="My account" size="small">
            <Row gutter={[8, 12]}>
              <Col span={12}>
                <Statistic title="Total invested" value={money(data.totalInvested)} valueStyle={{ fontSize: 16 }} />
              </Col>
              <Col span={12}>
                <Statistic title="Total withdrawn" value={money(data.totalWithdrawn)} valueStyle={{ fontSize: 16 }} />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Total profit earned"
                  value={money(data.totalAccruedProfit)}
                  valueStyle={{ fontSize: 16 }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Investing since"
                  value={data.firstInvestmentDate ? date(data.firstInvestmentDate) : '—'}
                  valueStyle={{ fontSize: 16 }}
                />
              </Col>
            </Row>

            <div style={{ marginTop: 16 }}>
              <Typography.Text style={{ fontSize: 12 }}>Profit paid out of profit earned</Typography.Text>
              <Progress
                percent={paidShare}
                strokeColor="#52c41a"
                format={(p) => `${p}%`}
              />
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={14}>
          <Card title="Profit paid to me — last 12 months" size="small">
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={data.monthlyPaidProfit} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tickLine={false} fontSize={12} />
                  <YAxis tickFormatter={(v: number) => money(v)} width={80} tickLine={false} fontSize={12} />
                  <Tooltip formatter={(v: number) => `PKR ${money(v)}`} />
                  <Bar dataKey="amount" fill="#52c41a" radius={[4, 4, 0, 0]} name="Profit paid" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
      </Row>

      <Card title="Capital paid in vs taken back — last 12 months" size="small" style={{ marginTop: 16 }}>
        <div style={{ width: '100%', height: 240 }}>
          <ResponsiveContainer>
            <BarChart data={data.monthlyCapitalFlow} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tickLine={false} fontSize={12} />
              <YAxis tickFormatter={(v: number) => money(v)} width={80} tickLine={false} fontSize={12} />
              <Tooltip formatter={(v: number) => `PKR ${money(v)}`} />
              <Legend />
              <Bar dataKey="given" fill="#1677ff" radius={[4, 4, 0, 0]} name="Capital paid in" />
              <Bar dataKey="received" fill="#fa8c16" radius={[4, 4, 0, 0]} name="Capital taken back" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card title="My recent transactions" size="small" style={{ marginTop: 16 }}>
        <Table<RecentTransaction>
          dataSource={data.recentTransactions}
          rowKey={(row, index) => `${row.type}-${row.date}-${index}`}
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'No transactions yet' }}
          columns={[
            { title: 'Date', dataIndex: 'date', render: date },
            {
              title: 'Type',
              dataIndex: 'type',
              render: (value: string) => <Tag color={TYPE_COLOR[value] ?? 'default'}>{value}</Tag>,
            },
            { title: 'Amount', dataIndex: 'amount', align: 'right', render: money },
          ]}
        />
      </Card>
    </>
  );
}
